import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { API_URL } from '../config/api';

export const MagicLoginPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithData } = useAuth();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [error, setError] = useState('');
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setError('Invalid magic link');
      return;
    }

    verifyMagicLink();
  }, [token]);

  const verifyMagicLink = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/auth/magic-login?token=${token}`,
        {
          method: 'POST',
          credentials: 'include',
        }
      );

      const data = await response.json();

      if (response.ok) {
        setStatus('success');
        toast.success('Logged in successfully!');
        loginWithData(data);
        setTimeout(() => navigate('/app'), 1500);
      } else {
        setStatus('error');
        setError(data.detail || 'Invalid or expired magic link');
        toast.error(data.detail || 'Failed to log in');
      }
    } catch (error) {
      setStatus('error');
      setError('An error occurred. Please try again.');
      toast.error('Connection error');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-10 h-10 bg-green-700 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            </div>
            <span className="text-xl font-bold text-green-800">TurfPulse™</span>
          </div>
          <CardTitle className="text-2xl">
            {status === 'verifying' && 'Verifying Magic Link...'}
            {status === 'success' && 'Welcome Back!'}
            {status === 'error' && 'Link Error'}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center">
          {status === 'verifying' && (
            <div>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-700 mx-auto mb-4"></div>
              <p className="text-gray-600">Please wait while we log you in...</p>
            </div>
          )}

          {status === 'success' && (
            <div>
              <div className="text-6xl mb-4">✨</div>
              <p className="text-gray-600 mb-4">You're being redirected to your dashboard...</p>
              <div className="animate-pulse text-green-700">Redirecting...</div>
            </div>
          )}

          {status === 'error' && (
            <div>
              <div className="text-6xl mb-4">❌</div>
              <p className="text-red-600 mb-6">{error}</p>
              <p className="text-gray-600 mb-6">
                Magic links expire after 15 minutes and can only be used once.
              </p>
              <Button 
                onClick={() => navigate('/')} 
                className="w-full bg-green-700 hover:bg-green-800"
                data-testid="back-to-home-btn"
              >
                Back to Home
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MagicLoginPage;
